import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Header from './Header';
import Footer from './Footer';
import theme, { mobileBreakpoint } from '../theme';

// Shared background container
const PageContainer = styled.div`
  background-color: ${props => props.theme.colors.background};
  min-height: 100vh;
  padding: 20px;
  position: relative;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0;
  padding-left: 20px;
  padding-right: 20px;
  @media (max-width: ${mobileBreakpoint}px) {
    margin-left: -10px;
    margin-right: -10px;
  }
`;

// Optional decorative blob
const Blob = styled.div`
  position: absolute;
  z-index: 0;
  pointer-events: none;
  filter: blur(0.5px);
  opacity: ${props => props.opacity || 0.22};
  background: ${props => props.bg};
  width: ${props => props.width || '420px'};
  height: ${props => props.height || '340px'};
  border-radius: ${props => props.radius || '60% 40% 50% 50% / 50% 60% 40% 50%'};
  top: ${props => props.top || 'unset'};
  left: ${props => props.left || 'unset'};
  right: ${props => props.right || 'unset'};
  bottom: ${props => props.bottom || 'unset'};
  transform: ${props => props.transform || 'none'};
  @media (max-width: ${mobileBreakpoint}px) {
    width: 220px;
    height: 160px;
  }
`;

// Shared CurvyLine styled-component
const CurvyLine = styled.div`
  position: absolute;
  width: ${props => props.width || '100px'};
  height: ${props => props.height || '100px'};
  border: 2px solid ${props => props.color || theme.colors.accent1};
  border-radius: 50%;
  border-bottom-color: transparent;
  border-left-color: transparent;
  transform: rotate(${props => props.rotate || '0deg'});
  top: ${props => props.top || '0'};
  left: ${props => props.left || '0'};
  opacity: ${props => props.opacity || 0.3};
  z-index: 0;
`;

/**
 * PageLayout component
 * @param {ReactNode} children - Page-specific content
 * @param {Array} blobs - Optional array of blob configs for decorative backgrounds
 * @param {boolean} showFooter - Whether to show the footer (default: true)
 * @param {Function} onTerminClick - Handler for "Termin Vereinbaren" click
 */
const PageLayout = ({ children, blobs = [], showFooter = true, onTerminClick }) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= mobileBreakpoint);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  return (
    <PageContainer>
      {/* Decorative blobs */}
      {blobs.map((blob, idx) => (
        <Blob key={idx} {...blob} />
      ))}
      {/* Shared CurvyLine for all pages (desktop only) */}
      {!isMobile && (
        <CurvyLine
          width="70vw"
          height="450px"
          color={theme.colors.accent2}
          top="35px"
          left="-10vw"
          rotate="-10deg"
          opacity={0.5}
          style={{ zIndex: 0 }}
        />
      )}
      <ContentContainer>
        <Header isMobile={isMobile} />
        {children}
        {showFooter && <Footer onTerminClick={onTerminClick} />}
      </ContentContainer>
    </PageContainer>
  );
};

export default PageLayout; 